import { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';

export interface QueueListItem {
  id: string; name: string; business: string; category: string;
  providers: number; capacity: number; booked: number; status: string;
}

export interface QueueFilters { businesses: string[]; categories: string[]; statuses: string[] }

interface ListState {
  items: QueueListItem[];
  filters: QueueFilters;
  loading: boolean;
  error: string | null;
}

export function useQueues() {
  const [state, setState] = useState<ListState>({
    items: [], filters: { businesses: [], categories: [], statuses: [] }, loading: true, error: null,
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.QUEUES, { params: { page: 1, page_size: 100 } });
      const data = res.data?.data ?? res.data;
      setState({ items: data.items, filters: data.filters, loading: false, error: null });
    } catch (err: any) {
      setState((s) => ({ ...s, loading: false, error: err?.response?.data?.message || 'Failed to load queues.' }));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  return { ...state, reload: load };
}

export interface QueueDetail {
  queue: Record<string, any>;
  tokens: Array<Record<string, any>>;
  providers: Array<Record<string, any>>;
  schedule: Array<Record<string, any>>;
}

// ---- Admin write operations (queue create / update / status / providers) ----

export interface ProviderOption { user_id: string; name: string; role: string }

export interface DaySchedule {
  day: string; is_enabled: boolean;
  start_time?: string; end_time?: string; max_tokens?: number;
}

export interface QueueWritePayload {
  name: string;
  provider_ids: string[];
  schedule: DaySchedule[];
  booking_mode?: string;
  same_for_all_days?: boolean;
  update_existing_slots?: boolean;
  cancel_exceeding_tokens?: boolean;
}

/** Team members of a business that can be assigned as queue providers. */
export async function fetchQueueProviders(businessId: string): Promise<ProviderOption[]> {
  const res = await axiosInstance.get(`${API_ENDPOINTS.QUEUES}/providers`, { params: { business_id: businessId } });
  return (res.data?.data ?? []) as ProviderOption[];
}

export async function createQueue(businessId: string, payload: QueueWritePayload): Promise<void> {
  await axiosInstance.post(API_ENDPOINTS.QUEUES, { business_id: businessId, ...payload });
}

export async function updateQueue(id: string, payload: Partial<QueueWritePayload>): Promise<void> {
  await axiosInstance.patch(`${API_ENDPOINTS.QUEUES}/${id}`, payload);
}

export async function setQueueStatus(id: string, active: boolean): Promise<void> {
  await axiosInstance.patch(`${API_ENDPOINTS.QUEUES}/${id}/status`, { active });
}

export function useQueueDetail(id: string | undefined) {
  const [data, setData] = useState<QueueDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`${API_ENDPOINTS.QUEUES}/${id}`);
      setData((res.data?.data ?? res.data) as QueueDetail);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load queue.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);
  return { data, loading, error, reload: load };
}
